/**
 * KOL-112: Verbrauch einer Organisation ansehen.
 *
 * Das Kontingent stand bisher ohne den Stand daneben — wer wissen wollte, wie
 * voll es ist, musste fragen. Hier stehen beide zusammen, und darunter der
 * Verlauf in der gewünschten Auflösung.
 *
 * Vorgänge sind die Größe, über die mit dem Kunden gesprochen wird.
 * LLM-Aufrufe und Tokens sind unsere interne Rechengrundlage — der Endpunkt
 * liefert sie nur Plattform-Admins, und diese Anzeige zeigt genau das, was
 * ankommt. Sie erfindet keine Spalte, die der Server weggelassen hat.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./Button";
import styles from "../pages/AdminPage.module.css";

export type Aufloesung = "stunde" | "tag" | "monat";

export interface ReihenPunkt {
  ab: string;
  /** Nur bei Monatsauflösung — gerundet wird einmal je Abrechnungszeitraum (KOL-133) */
  vorgaenge?: number;
  quota: number | null;
  /** Einkaufskosten in Cent */
  kostenCent?: number;
  llmAufrufe?: number;
  tokens?: { prompt: number; cached: number; completion: number };
}

export interface Verbrauchsreihe {
  aufloesung: Aufloesung;
  reihe: ReihenPunkt[];
  laufend: { zeitraum: string; ab: string; vorgaenge: number; quota: number | null };
  hinweise: string[];
  nichtZuordenbar?: { llmAufrufe: number; unbepreisteAufrufe?: number; modelle?: string[] };
}

/**
 * Spanne je Auflösung. Stundenwerte über Monate hinweg sind keine Auskunft,
 * sondern eine Wand aus Zahlen; der Server deckelt zusätzlich bei 14 Tagen.
 */
const SPANNE_TAGE: Record<Aufloesung, number> = { stunde: 7, tag: 60, monat: 730 };

const zahl = (n: number) => n.toLocaleString("de-DE");

/** Cent als Euro-Betrag — die Größe, in der seit KOL-133 gerechnet wird. */
export const euro = (cent: number) =>
  (cent / 100).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) +
  " €";

/** Beschriftung eines Punktes — die Auflösung bestimmt, was überhaupt zu sehen ist. */
export function punktLabel(ab: string, aufloesung: Aufloesung): string {
  const d = new Date(ab);
  if (aufloesung === "monat") {
    return d.toLocaleDateString("de-DE", { month: "long", year: "numeric", timeZone: "UTC" });
  }
  if (aufloesung === "tag") {
    return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "UTC" });
  }
  return `${d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", timeZone: "UTC" })} ${d
    .toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: "UTC" })} UTC`;
}

/** Anteil am Kontingent, oder null, wenn keins vereinbart ist. */
export function anteil(vorgaenge: number | undefined, quota: number | null): number | null {
  if (vorgaenge === undefined || quota === null || quota <= 0) return null;
  return Math.round((vorgaenge / quota) * 100);
}

export interface VerbrauchModalProps {
  orgId: string;
  orgName: string;
  onClose: () => void;
  /** nur für Tests: ersetzt den Abruf */
  lade?: (orgId: string, aufloesung: Aufloesung) => Promise<Verbrauchsreihe>;
}

export function VerbrauchModal({ orgId, orgName, onClose, lade }: VerbrauchModalProps) {
  const [aufloesung, setAufloesung] = useState<Aufloesung>("monat");
  const [daten, setDaten] = useState<Verbrauchsreihe | null>(null);
  const [fehler, setFehler] = useState("");
  const [laedt, setLaedt] = useState(true);

  const holen = useCallback(async () => {
    setLaedt(true);
    setFehler("");
    try {
      if (lade) {
        setDaten(await lade(orgId, aufloesung));
      } else {
        const bis = new Date();
        const von = new Date(bis.getTime() - SPANNE_TAGE[aufloesung] * 86_400_000);
        const q = `von=${von.toISOString()}&bis=${bis.toISOString()}&aufloesung=${aufloesung}`;
        setDaten(await api.get<Verbrauchsreihe>(`/admin/orgs/${orgId}/verbrauch?${q}`));
      }
    } catch (err) {
      setFehler((err as { message?: string }).message ?? "Verbrauch konnte nicht geladen werden");
      setDaten(null);
    } finally {
      setLaedt(false);
    }
  }, [orgId, aufloesung, lade]);

  useEffect(() => { void holen(); }, [holen]);

  // Vom Server geliefert oder nicht — die Spalten richten sich nach der Antwort.
  const intern = daten?.reihe.some((p) => p.llmAufrufe !== undefined) ?? false;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div
        className={styles.modalBox}
        style={{ maxWidth: "56rem", width: "min(56rem, 92vw)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3>Verbrauch — {orgName}</h3>

        {daten && (
          <p style={{ margin: "0 0 0.75rem" }}>
            Laufender Monat: <strong>{zahl(daten.laufend.vorgaenge)}</strong>
            {daten.laufend.quota !== null && (
              <> von {zahl(daten.laufend.quota)} Vorgängen
                {" "}({anteil(daten.laufend.vorgaenge, daten.laufend.quota)} %)</>
            )}
            {daten.laufend.quota === null && <> Vorgänge (kein Kontingent)</>}
          </p>
        )}

        <div style={{ display: "flex", gap: "0.25rem", marginBottom: "0.75rem" }}>
          {(["monat", "tag", "stunde"] as Aufloesung[]).map((a) => (
            <button
              key={a}
              type="button"
              className={styles.orgMoreChip}
              aria-pressed={aufloesung === a}
              style={aufloesung === a ? { fontWeight: 700 } : undefined}
              onClick={() => setAufloesung(a)}
            >
              {a === "monat" ? "Monate" : a === "tag" ? "Tage" : "Stunden"}
            </button>
          ))}
        </div>

        {fehler && <div className={styles.emptyHint}>{fehler}</div>}
        {laedt && !fehler && <div className={styles.loadingHint}>Wird geladen…</div>}

        {daten && !laedt && (
          <>
            {daten.hinweise.map((h) => (
              <p key={h} className={styles.tdMuted} style={{ fontSize: "0.8rem", margin: "0 0 0.5rem" }}>{h}</p>
            ))}

            <div className={styles.tableWrapper} style={{ maxHeight: "45vh", overflowY: "auto" }}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Zeitraum</th>
                    <th>{daten.aufloesung === "monat" ? "Vorgänge" : "Einkauf"}</th>
                    <th>Kontingent</th>
                    {intern && <th>LLM-Aufrufe</th>}
                    {intern && <th>Tokens (Prompt / davon Cache / Antwort)</th>}
                  </tr>
                </thead>
                <tbody>
                  {daten.reihe.map((p) => {
                    const a = anteil(p.vorgaenge, p.quota);
                    return (
                      <tr key={p.ab}>
                        <td className={styles.tdName}>{punktLabel(p.ab, daten.aufloesung)}</td>
                        {/* Vorgänge nur je Monat: Stunden- oder Tageswerte einzeln
                            aufzurunden ergäbe in Summe mehr als den Monat (KOL-133). */}
                        <td>{p.vorgaenge !== undefined ? zahl(p.vorgaenge) : euro(p.kostenCent ?? 0)}</td>
                        <td className={styles.tdMuted}>
                          {p.quota === null ? "—" : `${zahl(p.quota)}${a === null ? "" : ` (${a} %)`}`}
                        </td>
                        {intern && <td className={styles.tdMuted}>{zahl(p.llmAufrufe ?? 0)}</td>}
                        {intern && (
                          <td className={styles.tdMuted}>
                            {zahl(p.tokens?.prompt ?? 0)} / {zahl(p.tokens?.cached ?? 0)} / {zahl(p.tokens?.completion ?? 0)}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Systemweit, nicht auf diese Organisation bezogen: die Abfrage in
                usage.service.ts filtert nicht nach org_id. Ohne das Wort las sich
                die Zahl in einem Org-Modal wie ein Rest DIESES Kunden — bei einer
                Organisation mit leerer Tabelle besonders irreführend. */}
            {daten.nichtZuordenbar && daten.nichtZuordenbar.llmAufrufe > 0 && (
              <p className={styles.tdMuted} style={{ fontSize: "0.8rem", marginTop: "0.5rem" }}>
                Systemweit gehören im Zeitraum {zahl(daten.nichtZuordenbar.llmAufrufe)} LLM-Aufrufe
                keiner Organisation. Sie stecken in keiner Zeile oben und auch bei keinem anderen
                Kunden (KOL-113).
              </p>
            )}

            {/* Ein Modell ohne hinterlegten Preis kostet in der Rechnung nichts.
                Das muss auffallen, sonst läuft ein Kunde nach einem
                Modellwechsel gratis (KOL-133). */}
            {daten.nichtZuordenbar && (daten.nichtZuordenbar.unbepreisteAufrufe ?? 0) > 0 && (
              <p className={styles.tdMuted} style={{ fontSize: "0.8rem", marginTop: "0.5rem" }}>
                ⚠ Systemweit {zahl(daten.nichtZuordenbar.unbepreisteAufrufe!)} Aufrufe ohne
                hinterlegten Preis
                {daten.nichtZuordenbar.modelle?.length
                  ? ` (${daten.nichtZuordenbar.modelle.join(", ")})`
                  : ""}
                {" "}— die Kosten oben sind zu niedrig. Preis in <code>llm_preise</code> ergänzen.
              </p>
            )}
          </>
        )}

        <div className={styles.modalActions}>
          <Button variant="ghost" onClick={onClose}>Schließen</Button>
        </div>
      </div>
    </div>
  );
}
